import React, { ComponentProps, Key, useCallback, useState } from 'react'
import classnames from 'classnames'
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Area,
    BarProps,
    DotProps,
    Rectangle,
    Dot,
    Brush,
    Customized,
} from 'recharts'
import {
    NameType,
    Payload,
    ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import {
    ContentType,
    Payload as LegendPayload,
} from 'recharts/types/component/DefaultLegendContent'
import { getStrokeHexValue } from './getStrokeHexValue'
import { DefaultLegend } from '../../common/LineChartLegends/DefaultLegend'
import { extractKeysToPlot } from './extractKeysToPlot'
import { CustomTooltipWrapper } from './CustomTooltipWrapper'
import { TooltipPositionWrapper } from './TooltipPositionWrapper'
import { DataKey } from 'recharts/types/util/types'
import { TooltipProps } from 'recharts'
import { areArraysEqual } from '@/utils/areArraysEqual'

type renderTooltipItemFn = (
    entry: Payload<ValueType, NameType>
) => React.JSX.Element

type MultiChartProps = {
    showTooltip?: boolean | (() => boolean)
    data: Array<Record<string, unknown>>
    lineChartData?: Array<Record<string, unknown>>
    areaChartData?: Array<Record<string, unknown>>
    barChartData?: Array<Record<string, unknown>>
    xKey?: string
    yKey?: string
    keysToPlotRight?: string[]
    renderTooltipItem?: renderTooltipItemFn
    renderLegend?: ContentType
    tooltipItemsWrapperClassname?: string
    tooltipTitleFormatter?: (label: string) => string
    tooltipWrapper?: React.ComponentType<{
        label: string
        originalDateLabel?: string
        tooltipItemsWrapperClassname?: string
        children: React.ReactNode
    }>
    lineYAxisTickFormatter?: ComponentProps<typeof YAxis>['tickFormatter']
    barYAxisTickFormatter?: ComponentProps<typeof YAxis>['tickFormatter']
    areaYAxisTickFormatter?: ComponentProps<typeof YAxis>['tickFormatter']
    xAxisTickFormatter?: ComponentProps<typeof YAxis>['tickFormatter']
    xAxisInterval?: ComponentProps<typeof XAxis>['interval']
    xAxisDomain?: ComponentProps<typeof XAxis>['domain']
    xAxisScale?: ComponentProps<typeof XAxis>['scale']
    xAxisType?: ComponentProps<typeof XAxis>['type']
    xAxisTicks?: ComponentProps<typeof XAxis>['ticks']
    id?: string
    getLineProps?: (
        key: string,
        index: number
    ) => Record<string, unknown> | undefined
    getBarProps?: (
        key: string,
        index: number
    ) => Record<string, unknown> | undefined
    getAreaProps?: (
        key: string,
        index: number
    ) => Record<string, unknown> | undefined
    filterLineChartKeys?: (keys: string[]) => string[]
    datasetColorIndexMap?: Record<string, number>
    brushProps?: ComponentProps<typeof Brush>
    preventBrushFiltering?: boolean // When true, Brush won't filter the chart data (useful for minimaps)
    minHeight?: boolean
    highlightDotsOverlay?: React.FunctionComponent<Record<string, unknown>>
    allowOverflow?: boolean
}

export const MultiChart: React.FC<MultiChartProps> = (props) => {
    const {
        showTooltip = true,
        xKey,
        tooltipItemsWrapperClassname,
        tooltipWrapper,
        lineYAxisTickFormatter,
        barYAxisTickFormatter,
        areaYAxisTickFormatter,
        xAxisTickFormatter,
        xAxisInterval,
        xAxisDomain,
        xAxisScale,
        xAxisType,
        xAxisTicks,
        tooltipTitleFormatter = (label) => label,
        id,
        filterLineChartKeys,
        lineChartData = [],
        areaChartData = [],
        barChartData = [],
        datasetColorIndexMap = {},
        brushProps,
        minHeight = true,
        highlightDotsOverlay,
        allowOverflow = false,
    } = props

    let data = props.data

    const datasetsCount = Math.max(
        data.length,
        lineChartData.length,
        areaChartData.length,
        barChartData.length
    )

    if (props.data.length === 1) {
        const clone = Object.assign({}, data[0], { __clone: true })
        data = [clone, data[0], clone]
    }

    const shouldHideElement = (elementProps: {
        index?: Key
        dataKey: DataKey<unknown>
    }) => {
        const isClonedDataSet =
            datasetsCount === 1 &&
            ['0', '2'].includes(String(elementProps.index))

        const isClonedProperty = elementProps.dataKey === '__clone'

        return isClonedDataSet || isClonedProperty
    }

    // Add fake commodity cost for stacking
    areaChartData.forEach((dataItem) => {
        Object.assign(dataItem, {
            fakeTotalCommodityCost: dataItem['Total Commodity Cost ($)'],
        })
    })

    data.forEach((dataItem) => {
        Object.assign(dataItem, {
            fakeTotalCommodityCost: dataItem['Total Commodity Cost ($)'],
        })
    })

    const lineChartKeysToPlot = extractKeysToPlot(lineChartData, xKey)
    const lineChartFilteredKeys = filterLineChartKeys
        ? filterLineChartKeys(lineChartKeysToPlot)
        : lineChartKeysToPlot
    const areaChartFilteredKeys = extractKeysToPlot(areaChartData, xKey)
    const barChartFilteredKeys = extractKeysToPlot(barChartData, xKey)

    const axisLabelsClassname =
        'text-label-xsmall text-subtle whitespace-nowrap'

    const parentLineKeys = Object.keys(data[0] || {}).filter(
        (key) => key.includes('_line') && key !== 'date'
    )

    // Get consistent color index
    const getColorIndex = useCallback(
        (key: string, defaultIndex: number): number => {
            return datasetColorIndexMap[key] ?? defaultIndex
        },
        [datasetColorIndexMap]
    )

    // Getter functions with consistent colors
    const getLineProps = ({ key, index }: { key: string; index: number }) => {
        const colorIndex = getColorIndex(key, index)
        const strokeColor = getStrokeHexValue(colorIndex)

        return {
            yAxisId: 'line',
            stroke: strokeColor,
            dataKey: key,
            strokeWidth: 2,
            activeDot: {
                r: 8,
                fill: strokeColor,
                stroke: 'white',
                strokeWidth: 3,
            },
            dot: {
                r: 0,
                fill: strokeColor,
            },
        }
    }

    const getAreaProps = ({ key, index }: { key: string; index: number }) => {
        const colorIndex = getColorIndex(key, index)
        const fillColor = getStrokeHexValue(colorIndex)

        return {
            yAxisId: 'area',
            dataKey: key,
            stroke: fillColor,
            fill: `url(#gradient-${colorIndex.toString()})`,
            strokeWidth: 2,
            activeDot: {
                r: 8,
                fill: fillColor,
                stroke: 'white',
                strokeWidth: 3,
            },
            dot: {
                r: 0,
                fill: fillColor,
                fillOpacity: 1,
            },
        }
    }

    const getBarProps = ({ key, index }: { key: string; index: number }) => {
        const colorIndex = getColorIndex(key, index)
        const fillColor = getStrokeHexValue(colorIndex)

        return {
            dataKey: key,
            yAxisId: 'bar',
            radius: [4, 4, 0, 0] as [number, number, number, number],
            fill: fillColor,
            barSize: 16,
            activeBar: CustomActiveBar as unknown as Bar,
        }
    }

    const renderTooltip = useCallback(
        (params: TooltipProps<ValueType, NameType>) => {
            const { payload, coordinate } = params

            if (!payload || payload.length === 0) {
                return null
            }

            // If coordinate is not available, don't render tooltip
            // Recharts needs coordinate to position the tooltip correctly
            if (!coordinate || coordinate.x === undefined) {
                return null
            }

            // Check if current data point is cloned - if so, don't show tooltip
            const isClonedDataPoint = payload.some(
                (entry) =>
                    typeof entry.payload === 'object' &&
                    entry.payload !== null &&
                    '__clone' in entry.payload &&
                    (entry.payload as { __clone?: boolean }).__clone
            )

            if (isClonedDataPoint) {
                return null
            }

            const customRenderTooltipItem = (
                entry: Payload<ValueType, NameType>
            ): React.JSX.Element => {
                const dataKey = entry.dataKey as string

                let dataIndex = 0

                if (lineChartFilteredKeys.includes(dataKey)) {
                    dataIndex = lineChartFilteredKeys.indexOf(dataKey)
                } else if (barChartFilteredKeys.includes(dataKey)) {
                    dataIndex = barChartFilteredKeys.indexOf(dataKey)
                } else if (areaChartFilteredKeys.includes(dataKey)) {
                    dataIndex = areaChartFilteredKeys.indexOf(dataKey)
                }

                const colorIndex = getColorIndex(dataKey, dataIndex)
                const color = getStrokeHexValue(colorIndex)

                if (!dataKey) {
                    return <></>
                }

                if (props.renderTooltipItem) {
                    const modifiedEntry = {
                        ...entry,
                        color: color,
                    } as Payload<ValueType, NameType>
                    return props.renderTooltipItem(modifiedEntry)
                }

                return (
                    <>
                        <div className="flex items-center gap-1">
                            <div
                                className="h-2 w-2 shrink-0 rounded-full bg-[currentColor]"
                                style={{
                                    color: color,
                                }}
                            />
                            <div className="text-label-xsmall whitespace-nowrap">
                                {entry.name}
                            </div>
                        </div>
                        <div className="text-body-small ml-4 font-bold">
                            {entry.value}
                        </div>
                    </>
                )
            }

            const filteredPayload = payload.filter(
                (entry) =>
                    entry.dataKey !== 'fakeTotalCommodityCost' &&
                    !(
                        typeof entry.dataKey === 'string' &&
                        entry.dataKey.includes('_line')
                    )
            )
            const label = params.label
                ? tooltipTitleFormatter(String(params.label))
                : ''

            if (!showTooltip) return null

            if (typeof showTooltip === 'function' && !showTooltip()) return null

            const TooltipWrapperComponent =
                tooltipWrapper || CustomTooltipWrapper

            // Get the original date value
            const originalDateLabel = params.label ? String(params.label) : ''

            const tooltipContent = (
                <TooltipWrapperComponent
                    label={label}
                    originalDateLabel={originalDateLabel}
                    tooltipItemsWrapperClassname={tooltipItemsWrapperClassname}
                >
                    <TooltipItems
                        payload={filteredPayload}
                        renderTooltipItem={customRenderTooltipItem}
                    />
                </TooltipWrapperComponent>
            )

            return (
                <TooltipPositionWrapper tooltipProps={params}>
                    {tooltipContent}
                </TooltipPositionWrapper>
            )
        },
        [
            tooltipTitleFormatter,
            showTooltip,
            tooltipWrapper,
            tooltipItemsWrapperClassname,
            lineChartFilteredKeys,
            barChartFilteredKeys,
            areaChartFilteredKeys,
            getColorIndex,
            props,
        ]
    ) as ComponentProps<typeof Tooltip>['content']

    const [legendPayload, setLegendPayload] = useState<LegendPayload[]>([])

    // Helper to check if payloads are equal by comparing dataKeys
    const arePayloadsEqual = (
        a: LegendPayload[],
        b: LegendPayload[]
    ): boolean => {
        const aKeys = a.map((p) => p.dataKey)
        const bKeys = b.map((p) => p.dataKey)
        return areArraysEqual(aKeys, bKeys)
    }

    // Filter payload to remove unwanted entries
    const filterPayload = (payload: LegendPayload[]): LegendPayload[] => {
        return payload.filter(
            (entry) =>
                entry.dataKey !== '__clone' &&
                entry.dataKey !== 'fakeTotalCommodityCost' &&
                !(
                    typeof entry.dataKey === 'string' &&
                    entry.dataKey.includes('_line')
                )
        )
    }

    const createLegendContent = useCallback(
        (payload: LegendPayload[]) => {
            if (payload.length === 0) return null

            // Use custom renderLegend if provided, otherwise use default
            return props.renderLegend ? (
                typeof props.renderLegend === 'function' ? (
                    props.renderLegend({ payload })
                ) : (
                    props.renderLegend
                )
            ) : (
                <div className="flex items-start justify-between gap-4 pb-8">
                    <DefaultLegend payload={payload} />
                </div>
            )
        },
        [props]
    )

    const renderLegend = useCallback(
        (renderLegendProps: { payload?: LegendPayload[] }) => {
            const payload = renderLegendProps.payload

            if (!payload) {
                return null
            }

            const filteredPayload = filterPayload(payload)

            setLegendPayload((prev) => {
                if (arePayloadsEqual(prev, filteredPayload)) {
                    return prev
                }
                return filteredPayload
            })

            return null
        },
        []
    )

    const renderLegendOutside = () => {
        return createLegendContent(legendPayload)
    }

    const wrapperClassName = classnames('shrink-0 grow overflow-hidden', {
        'basis-chartSurfaceMinHeight': minHeight,
        'basis-0': !minHeight,
    })

    const baseMargin = { top: 20, right: 5, bottom: 20, left: 5 }
    const overflowPadding = allowOverflow ? 7 : 0
    const margin = {
        top: baseMargin.top + overflowPadding,
        right: baseMargin.right + overflowPadding,
        bottom: baseMargin.bottom + overflowPadding,
        left: baseMargin.left + overflowPadding,
    }

    return (
        <div className="flex h-full w-full flex-col">
            {legendPayload.length > 0 && (
                <div className="flex-shrink-0">{renderLegendOutside()}</div>
            )}
            <div className={wrapperClassName}>
                <ResponsiveContainer width="100%" height="100%" id={id}>
                    <ComposedChart
                        data={data}
                        barCategoryGap={0}
                        margin={margin}
                    >
                        <defs>
                            {Array.from(new Array(20)).map((_key, index) => {
                                const gradientId = `gradient-${index.toString()}`
                                const strokeColor =
                                    getStrokeHexValue(index) || ''
                                return (
                                    <linearGradient
                                        key={gradientId}
                                        id={gradientId}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor={strokeColor}
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor={strokeColor}
                                            stopOpacity={0.2}
                                        />
                                    </linearGradient>
                                )
                            })}
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            className={'stroke-neutral-200'}
                        />
                        <XAxis
                            dataKey={xKey}
                            axisLine={false}
                            tickLine={false}
                            dy={16}
                            className={axisLabelsClassname}
                            domain={xAxisDomain}
                            scale={xAxisScale}
                            type={xAxisType}
                            ticks={xAxisTicks}
                            tickFormatter={(value, index) => {
                                // Hide ticks for cloned datasets when data length is 1
                                if (
                                    datasetsCount === 1 &&
                                    [0, 2].includes(index)
                                ) {
                                    return ''
                                }
                                return xAxisTickFormatter
                                    ? xAxisTickFormatter(value, index)
                                    : String(value)
                            }}
                            interval={xAxisInterval}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickMargin={10}
                            yAxisId="line"
                            orientation="left"
                            textAnchor="start"
                            className={axisLabelsClassname}
                            tickFormatter={lineYAxisTickFormatter}
                            dx={-43}
                            dy={-9}
                        />
                        {barChartFilteredKeys.length > 0 && (
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                yAxisId="bar"
                                orientation="left"
                                mirror={true}
                                textAnchor="start"
                                className={axisLabelsClassname}
                                tickFormatter={barYAxisTickFormatter}
                                dx={-67}
                                dy={9}
                            />
                        )}
                        {areaChartFilteredKeys.length > 0 && (
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickMargin={10}
                                yAxisId="area"
                                orientation="right"
                                textAnchor="end"
                                dx={45}
                                className={axisLabelsClassname}
                                tickFormatter={areaYAxisTickFormatter}
                            />
                        )}
                        <Tooltip content={renderTooltip} />
                        <Legend
                            layout="horizontal"
                            align="left"
                            verticalAlign="top"
                            iconType="circle"
                            iconSize={10}
                            content={renderLegend}
                        />
                        {parentLineKeys.map((key, index) => {
                            const displayKey = key.replace('_line', '')
                            const colorIndex = getColorIndex(displayKey, index)
                            const strokeColor = getStrokeHexValue(colorIndex)
                            return (
                                <Line
                                    key={`parent-line-${key}`}
                                    yAxisId="area"
                                    stroke={strokeColor}
                                    dataKey={key}
                                    strokeWidth={3}
                                    activeDot={false}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            )
                        })}

                        {areaChartFilteredKeys.length > 0 && (
                            <Area
                                {...getAreaProps({
                                    key: 'fakeTotalCommodityCost',
                                    index: 0,
                                })}
                                {...props.getAreaProps?.(
                                    'fakeTotalCommodityCost',
                                    0
                                )}
                                activeDot={false}
                                fillOpacity={0}
                            />
                        )}

                        {areaChartFilteredKeys
                            .filter((key) => key !== 'fakeTotalCommodityCost')
                            .map((key, index) => {
                                const areaProps = getAreaProps({ key, index })
                                const customProps = props.getAreaProps?.(
                                    key,
                                    index
                                )
                                const mergedProps = {
                                    ...areaProps,
                                    ...customProps,
                                }
                                return (
                                    <Area
                                        key={key}
                                        {...mergedProps}
                                        isAnimationActive={false}
                                        activeDot={(dotProps: unknown) => {
                                            const { index, dataKey, cx, cy } =
                                                dotProps as DotProps & {
                                                    index?: number
                                                    dataKey?: string
                                                }
                                            return (
                                                <CustomActiveDot
                                                    cx={cx}
                                                    cy={cy}
                                                    r={mergedProps.activeDot.r}
                                                    fill={
                                                        mergedProps.activeDot
                                                            .fill
                                                    }
                                                    stroke={
                                                        mergedProps.activeDot
                                                            .stroke
                                                    }
                                                    strokeWidth={Number(
                                                        mergedProps.activeDot
                                                            .strokeWidth
                                                    )}
                                                    hide={shouldHideElement({
                                                        index: index,
                                                        dataKey: dataKey || '',
                                                    })}
                                                />
                                            )
                                        }}
                                    />
                                )
                            })}

                        {barChartFilteredKeys.map((key, index) => {
                            const barProps = getBarProps({ key, index })
                            const customProps = props.getBarProps?.(key, index)
                            const mergedProps = {
                                ...barProps,
                                ...customProps,
                            }

                            return (
                                <Bar
                                    key={key}
                                    {...mergedProps}
                                    isAnimationActive={false}
                                    shape={(barShapeProps: BarProps) => {
                                        return (
                                            <Rectangle
                                                x={Number(barShapeProps.x)}
                                                y={Number(barShapeProps.y)}
                                                width={Number(
                                                    barShapeProps.width
                                                )}
                                                height={Number(
                                                    barShapeProps.height
                                                )}
                                                fill={mergedProps.fill}
                                                radius={mergedProps.radius}
                                                opacity={
                                                    shouldHideElement(
                                                        barShapeProps
                                                    )
                                                        ? 0
                                                        : 1
                                                }
                                            />
                                        )
                                    }}
                                    activeBar={(barShapeProps: BarProps) => {
                                        return (
                                            <CustomActiveBar
                                                {...barShapeProps}
                                                hide={shouldHideElement(
                                                    barShapeProps
                                                )}
                                            />
                                        )
                                    }}
                                />
                            )
                        })}
                        {lineChartFilteredKeys.map((key, index) => {
                            const lineProps = getLineProps({ key, index })
                            const customProps = props.getLineProps?.(key, index)
                            const mergedProps = {
                                ...lineProps,
                                ...customProps,
                            }
                            return (
                                <Line
                                    key={key}
                                    {...mergedProps}
                                    activeDot={(dotProps: unknown) => {
                                        const { index, dataKey, cx, cy } =
                                            dotProps as DotProps & {
                                                index?: number
                                                dataKey?: string
                                            }
                                        return (
                                            <CustomActiveDot
                                                cx={cx}
                                                cy={cy}
                                                r={mergedProps.activeDot.r}
                                                fill={
                                                    mergedProps.activeDot.fill
                                                }
                                                stroke={
                                                    mergedProps.activeDot.stroke
                                                }
                                                strokeWidth={Number(
                                                    mergedProps.activeDot
                                                        .strokeWidth
                                                )}
                                                hide={shouldHideElement({
                                                    index: index,
                                                    dataKey: dataKey || '',
                                                })}
                                            />
                                        )
                                    }}
                                    isAnimationActive={false}
                                />
                            )
                        })}
                        {highlightDotsOverlay && (
                            <Customized component={highlightDotsOverlay} />
                        )}
                        {brushProps && (
                            <Brush
                                {...(brushProps as Omit<
                                    ComponentProps<typeof Brush>,
                                    'ref'
                                >)}
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

type TooltipItemsProps = {
    payload: Payload<ValueType, NameType>[]
    renderTooltipItem: renderTooltipItemFn
}

const TooltipItems: React.FC<TooltipItemsProps> = (props) => {
    const { payload, renderTooltipItem } = props

    return payload.map((entry, index) => {
        if (entry.dataKey === '__clone') {
            return null
        }
        return (
            <React.Fragment key={index}>
                {renderTooltipItem(entry)}
            </React.Fragment>
        )
    })
}

// Custom shape for active bar with dot
const CustomActiveBar = (props: {
    fill?: string
    x?: string | number
    y?: string | number
    width?: string | number
    height?: string | number
    hide?: boolean
}) => {
    const { fill, hide } = props
    const x = Number(props.x) || 0
    const y = Number(props.y) || 0
    const width = Number(props.width) || 0
    const height = Number(props.height) || 0

    return (
        <g>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={fill}
                stroke="white"
                strokeWidth={2}
                opacity={hide ? 0 : 0.6}
            />
            <CustomActiveDot
                cx={x + width / 2}
                cy={y - 4}
                r={8}
                fill={fill}
                stroke="white"
                strokeWidth={3}
                hide={hide}
            />
        </g>
    )
}

const CustomActiveDot: React.FC<{
    cx?: number
    cy?: number
    r?: number
    fill?: string
    stroke?: string
    strokeWidth?: number
    hide?: boolean
}> = ({ cx, cy, r = 8, fill, stroke = 'white', strokeWidth = 3, hide }) => {
    return (
        <Dot
            cx={cx}
            cy={cy}
            r={r}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            opacity={hide ? 0 : 1}
            style={{
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
            }}
        />
    )
}
